var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useFocusEffect } from '@react-navigation/native';
import { FlashList } from '@shopify/flash-list';
import { useInfiniteQuery } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { Dimensions, Image, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import BookmarkFilledIcon from '@/assets/icon/ic_bookmark_filled.svg';
import ChevronRightIcon from '@/assets/icon/ic_chevron_right.svg';
import StoreAddressFillIcon from '@/assets/icon/ic_store_address_fill.svg';
import { SccPressable } from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { PlaceListTypeDto } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import useNavigation from '@/navigation/useNavigation';
import SearchLoading from '@/screens/SearchScreen/components/SearchLoading';
const BANNER_WIDTH = Math.min(350, Dimensions.get('window').width - 40);
const BANNER_HEIGHT = (BANNER_WIDTH / 350) * 67;
const ESTIMATED_ITEM_HEIGHT = 80;
const BANNER_GAP = 16;
export default function SavedPlaceListsTab() {
    var _a;
    const navigation = useNavigation();
    const { api } = useAppComponents();
    const insets = useSafeAreaInsets();
    const [containerHeight, setContainerHeight] = useState(0);
    const bannerBottomOffset = insets.bottom + 80;
    const bannerTotalHeight = BANNER_HEIGHT + BANNER_GAP;
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch, } = useInfiniteQuery({
        queryKey: ['SavedPlaceLists'],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ pageParam }) {
            return (yield api.listSavedPlaceLists({
                cursor: pageParam !== null && pageParam !== void 0 ? pageParam : undefined,
                limit: 20,
            })).data;
        }),
        getNextPageParam: lastPage => {
            var _a;
            return (_a = lastPage.cursor) !== null && _a !== void 0 ? _a : undefined;
        },
        initialPageParam: undefined,
    });
    // PlaceListDetail 에서 save/unsave 직후 useSavePlaceList 는 invalidate 하지 않으므로
    // (cross-cutting concern 회피), 이 화면이 focus 될 때마다 자체적으로 refetch 한다.
    useFocusEffect(useCallback(() => {
        refetch();
    }, [refetch]));
    const placeLists = (_a = data === null || data === void 0 ? void 0 : data.pages.flatMap(page => { var _a; return (_a = page.items) !== null && _a !== void 0 ? _a : []; })) !== null && _a !== void 0 ? _a : [];
    // 리스트 아이템이 배너 영역과 겹치는지 판단
    const estimatedContentHeight = placeLists.length * ESTIMATED_ITEM_HEIGHT;
    const bannerAreaTop = containerHeight - bannerBottomOffset - bannerTotalHeight;
    const shouldInlineBanner = containerHeight > 0 && estimatedContentHeight > bannerAreaTop;
    const handleItemPress = (item) => {
        if (item.type === PlaceListTypeDto.MyPlaces) {
            navigation.navigate('FavoritePlaces');
            return;
        }
        navigation.navigate('PlaceListDetail', { placeListId: item.id });
    };
    const handleBannerPress = () => {
        navigation.navigate('Webview', {
            url: 'https://forms.staircrusher.club/list-request',
        });
    };
    const renderBanner = () => (_jsx(BannerWrapper, { children: _jsx(SccPressable, { elementName: "saved_place_lists_request_banner", onPress: handleBannerPress, children: _jsx(BannerImage, { source: require('@/assets/img/saved_place_lists_banner.png'), resizeMode: "contain" }) }) }));
    if (isLoading) {
        return _jsx(SearchLoading, {});
    }
    if (isError) {
        return (_jsx(NoResultContainer, { children: _jsx(NoResultText, { children: "\uB9AC\uC2A4\uD2B8\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." }) }));
    }
    if (placeLists.length === 0) {
        return (_jsx(NoResultContainer, { children: _jsx(NoResultText, { children: "\uC800\uC7A5\uD55C \uC7A5\uC18C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }) }));
    }
    return (_jsxs(ListContainer, { onLayout: e => setContainerHeight(e.nativeEvent.layout.height), children: [_jsx(FlashList, { contentContainerStyle: {
                    backgroundColor: color.white,
                    paddingBottom: shouldInlineBanner
                        ? BANNER_GAP
                        : bannerBottomOffset + bannerTotalHeight,
                }, data: placeLists, keyExtractor: item => item.id, renderItem: ({ item, index }) => {
                    var _a, _b;
                    return (_jsx(SccPressable, { elementName: "saved_place_list_item", logParams: { placeListId: item.id }, onPress: () => handleItemPress(item), children: _jsxs(ItemWrapper, { isFirst: index === 0, children: [_jsxs(IconTextGroup, { children: [((_a = item.nameChip) === null || _a === void 0 ? void 0 : _a.iconUrl) ? (
                                        // 아이콘이 있으면 원형 배경을 씌우지 않고 이미지가 그 자리를
                                        // 그대로 대체한다 — 업로드 자체를 원형으로 하는 것이 전제다.
                                        _jsx(SccRemoteImage, { imageUrl: item.nameChip.iconUrl, style: ICON_IMAGE_STYLE, wrapperBackgroundColor: null })) : (_jsx(IconCircle, { bgColor: item.type === PlaceListTypeDto.MyPlaces
                                                ? '#67AEFF'
                                                : ((_b = item.iconColor) !== null && _b !== void 0 ? _b : '#FFC01E'), children: _jsx(BookmarkFilledIcon, { width: 20, height: 20, color: color.white }) })), _jsxs(ItemContent, { children: [_jsx(ItemName, { numberOfLines: 1, children: item.name }), _jsxs(ItemPlaceCountRow, { children: [_jsx(StoreAddressFillIcon, { width: 16, height: 16, color: color.gray30v2 }), _jsxs(ItemPlaceCount, { children: [item.placeCount, "\uACF3"] })] })] })] }), _jsx(ChevronRightIcon, { width: 20, height: 20, color: "#B4B4C0" })] }) }));
                }, ListFooterComponent: shouldInlineBanner ? renderBanner : undefined, estimatedItemSize: ESTIMATED_ITEM_HEIGHT, onEndReached: () => {
                    if (hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                    }
                }, onEndReachedThreshold: 0.5 }), !shouldInlineBanner && (_jsx(View, { style: {
                    position: 'absolute',
                    bottom: bannerBottomOffset,
                    left: 0,
                    right: 0,
                    alignItems: 'center',
                }, children: renderBanner() }))] }));
}
const ListContainer = styled.View `
  flex: 1;
`;
const NoResultContainer = styled.View `
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;
const NoResultText = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardMedium};
  padding-bottom: 120px;
  color: ${() => color.gray50};
`;
const ItemWrapper = styled.View `
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 16px;
  padding-vertical: 20px;
  border-top-width: ${({ isFirst }) => (isFirst ? '0' : '1px')};
  border-top-color: #f2f2f5;
  gap: 8px;
`;
const IconTextGroup = styled.View `
  flex: 1;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
`;
// overflow: hidden이 있어야 borderRadius가 실제로 클리핑된다 (SccRemoteImage의
// ImageWrapper에는 없다). 업로드 이미지가 원형이 아니어도 사각형으로 튀지 않는다.
const ICON_IMAGE_STYLE = {
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'hidden',
};
const IconCircle = styled.View `
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${({ bgColor }) => bgColor};
  align-items: center;
  justify-content: center;
`;
const ItemContent = styled.View `
  flex: 1;
  gap: 2px;
`;
const ItemName = styled.Text `
  font-size: 16px;
  font-family: ${() => font.pretendardRegular};
  color: #16181c;
  line-height: 24px;
  letter-spacing: -0.32px;
`;
const ItemPlaceCountRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;
const ItemPlaceCount = styled.Text `
  font-size: 13px;
  font-family: ${() => font.pretendardRegular};
  color: #a0a2ae;
  line-height: 18px;
  letter-spacing: -0.26px;
`;
const BannerWrapper = styled.View `
  align-items: center;
  padding-vertical: ${BANNER_GAP}px;
`;
const BannerImage = styled(Image) `
  width: ${BANNER_WIDTH}px;
  height: ${BANNER_HEIGHT}px;
  border-radius: 6px;
`;
